<template>
  <ModalStep
    :id="'mfa-setup-modal-content'"
    data-test-id="mfa-setup-modal-content"
    :header="{
      title: t('auth.mfa.setup.title'),
    }"
  >
    <template #bodyContent>
      <form>
        <div class="mfa-setup-steps">
          <!-- Step 1: QR Code -->
          <div class="mfa-step">
            <h3>{{ t('auth.mfa.setup.step1.title') }}</h3>
            <p class="mfa-step-description">{{ t('auth.mfa.setup.step1.description') }}</p>
            <div v-if="qrCodeRef" class="qr-code-container">
              <img :src="qrCodeRef" data-test-id="mfa-setup-qr-code" alt="QR Code" />
            </div>
            <div v-else class="loading">{{ t('ui.message.loading') }}</div>

            <!-- Manual Entry Key -->
            <div v-if="manualEntryKeyRef" class="manual-entry">
              <p class="manual-entry-label">{{ t('auth.mfa.setup.step1.manualEntry') }}</p>
              <code class="manual-entry-key" data-test-id="mfa-setup-manual-key">{{ manualEntryKeyRef }}</code>
            </div>
          </div>

          <!-- Step 2: Backup Codes -->
          <div v-if="backupCodesRef && backupCodesRef.length > 0" class="mfa-step">
            <h3>{{ t('auth.mfa.setup.step2.title') }}</h3>
            <p class="mfa-step-description">{{ t('auth.mfa.setup.step2.description') }}</p>
            <div class="backup-codes">
              <code v-for="(code, index) in backupCodesRef" :key="index">{{ code }}</code>
            </div>
          </div>

          <!-- Step 3: Verification -->
          <div class="mfa-step">
            <h3>{{ t('auth.mfa.setup.step3.title') }}</h3>
            <p class="mfa-step-description">{{ t('auth.mfa.setup.step3.description') }}</p>
            <CustomInput
              id="mfa-code"
              data-test-id="mfa-setup-code"
              type="text"
              :label="t('auth.mfa.setup.input.code.label')"
              :placeholder="t('auth.mfa.setup.input.code.placeholder')"
              :value="codeRef"
              @emit:change="codeChangeHandler"
              :errors="codeErrorRef"
              :maxlength="6"
            />
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          data-test-id="cancel-mfa-setup-button"
          color="white"
          @emit:click="closeModal"
          :text="t('ui.button.cancel')"
        ></CustomButton>
        <CustomButton
          usage="button"
          type="button"
          data-test-id="confirm-mfa-setup-button"
          color="primary"
          @emit:click="submitForm"
          :isDisabled="!codeRef || codeRef.length !== 6"
          :isLoading="isPending"
          :text="t('ui.button.confirm')"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { onMounted, ref } from 'vue'

import { mfaEnable, mfaSetup } from '@/features/auth/api'
import { V1_USER_DATA_QUERY_KEY, V1_AUTH_USER_QUERY_KEY } from '@/features/auth/hooks/use-account.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const qrCodeRef = ref<string | null>(null)
const secretRef = ref<string | null>(null)
const manualEntryKeyRef = ref<string | null>(null)
const backupCodesRef = ref<string[]>([])
const codeRef = ref<string>('')
const codeErrorRef = ref<string>('')

// Fetch MFA setup data on mount
onMounted(async () => {
  try {
    const data = await mfaSetup()
    qrCodeRef.value = data.qrCode
    secretRef.value = data.secret
    manualEntryKeyRef.value = data.manualEntryKey
    backupCodesRef.value = data.backupCodes
  } catch (error) {
    console.error('Error fetching MFA setup:', error)
    toast?.error(t('error.common'))
    closeModal()
  }
})

const { mutate: enableMfa, isPending } = useMutation({
  mutationFn: mfaEnable,
  onError: (error: any) => {
    if (error.message?.includes('Invalid') || error.status === 400) {
      codeErrorRef.value = t('auth.mfa.setup.error')
    } else {
      toast?.error(t('error.common'))
    }
  },
  onSuccess: async () => {
    toast?.success(t('auth.mfa.setup.success'))
    // Invalidate account queries to refresh mfaEnabled status
    await queryClient.invalidateQueries({ queryKey: [V1_USER_DATA_QUERY_KEY] })
    await queryClient.invalidateQueries({ queryKey: [V1_AUTH_USER_QUERY_KEY] })
    modalsStore.toggleModal('mfa-setup', false)
  },
})

function codeChangeHandler(value: string) {
  codeRef.value = value
  codeErrorRef.value = ''
}

function submitForm() {
  if (!codeRef.value || codeRef.value.length !== 6) {
    codeErrorRef.value = t('auth.mfa.setup.error')
    return
  }

  enableMfa({ code: codeRef.value })
}

function closeModal() {
  modalsStore.toggleModal('mfa-setup', false)
}
</script>

<style lang="scss" scoped>
.mfa-setup-steps {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.mfa-step {
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .mfa-step-description {
    font-size: 0.9rem;
    color: var(--color-text-secondary);
    margin-bottom: 1rem;
  }
}

.qr-code-container {
  display: flex;
  justify-content: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--color-border);

  img {
    max-width: 200px;
    height: auto;
  }
}

.manual-entry {
  margin-top: 1rem;
  text-align: center;

  .manual-entry-label {
    font-size: 0.85rem;
    color: var(--color-text-secondary);
    margin-bottom: 0.5rem;
  }

  .manual-entry-key {
    display: block;
    font-family: monospace;
    font-size: 1rem;
    font-weight: 600;
    padding: 0.75rem;
    background: var(--color-background-secondary);
    border-radius: 4px;
    border: 1px solid var(--color-border);
    letter-spacing: 0.1em;
    user-select: all;
    cursor: pointer;

    &:hover {
      background: var(--color-background-hover, #f0f0f0);
    }
  }
}

.backup-codes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  padding: 1rem;
  background: var(--color-background-secondary);
  border-radius: 8px;

  code {
    font-family: monospace;
    font-size: 0.9rem;
    padding: 0.5rem;
    background: white;
    border-radius: 4px;
    border: 1px solid var(--color-border);
    text-align: center;
  }
}

.loading {
  text-align: center;
  padding: 2rem;
  color: var(--color-text-secondary);
}
</style>
